import dayjs from 'dayjs'

export enum RepaymentTypeEnum {
	EqualPayments = 'EqualPayments',
	EqualPrincipal = 'EqualPrincipal',
}

export enum LoanTypeEnum {
	ASSET_FORMATION = 'ASSET_FORMATION',
	PAYMENT_REDUCTION = 'PAYMENT_REDUCTION', // B
}

export enum LoanTypeEnumCamelCase {
	AssetFormation = 'AssetFormation',
	PaymentReduction = 'PaymentReduction',
}

export const DefaultInterestRates = {
	interestRate: 0.085,
	additionalFee: 0.014,
	futureAdditionalFee: 0.03,
}

export type LoanCalculationInputs = {
	loanAmount: number // Upphæð láns - loan amount
	principalPaymentType?: RepaymentTypeEnum // Greiðslufyrirkomulag
	firstPaymentDate?: string // opcionalno, datum prve uplate
	propertyMarketValueAmount: number // Markaðsvirði húsnæðis
	annualHousingPriceIncreasePercentage: number // Áætluð árshækkun húsnæðis
	optionBAmount: number // Tímabundin lækkun á mánaðargreiðslum
	optionBLengthInYears: number // Lengd lækkunar

	estimatedPropertyAnnual10YearOption: boolean // - kako se ovo šalje ili kaj ovi gumbi rade?
	selectedPlan?: LoanTypeEnum // Valinn greiðsluáætlun
	numberOfPaymentsMonths: number // Lánstími - treba * 12 da bude po mjesecima
	fixedInterestPercentage?: number // Vextir - postotak
	extraMonthlyPaymentToPrincipal: number // Skattfrjáls ráðstöfun á höfuðstól

	interestRateOptionFixed: boolean // radiobutton for interest rate type
	useCustomInterestRate?: boolean // true when user manually typed the interest rate; false when loan amount/market value changed last
}

export interface LoanCalculationRequest {
	loanAmount: number
	principalPaymentType?: RepaymentTypeEnum
	numberOfPaymentsMonths: number
	extraMonthlyPaymentToPrincipal: number
	propertyMarketValueAmount: number
	annualHousingPriceIncreasePercentage?: number
	paymentReduction?: {
		amount: number
		lengthInMonths: number
	}
	useRealValues: boolean

	// new fields for future interest rates
	interestRates?: {
		interestRatePercentage: number
		validFromInstallment: number
	}[]
	doubleCaCost?: boolean
}

export interface LoanCalculationResponse {
	calcDate: string
	disbursement: number
	disbursementFee: number
	totalCost: number
	totalPaymentFee: number
	totalInterest: number
	totalPrincipal: number
	totalRepayment: number
	interestRate: number
	loanLengthInYears: number
	loanLengthInMonths: number
	avgPayment: number
	firstInterestDate: string
	nextPayment: number
	nextPaymentDate: string
	installments: {
		number: number
		dueDate: string
		principal: number
		interest: number
		paymentFee: number
		total: number
		remainingPrincipal: number
		deferredInterest: number
		propertyValue: number
		propertyEquity: number
	}[]
	annualPercentageRate: number
	settlementCosts: CalculatorDataSettlementCosts[]
	paymentReductionMinAmount: number
	paymentReductionMaxAmount: number
	interestRates?: {
		interestRate: number
		validFromInstallment: number
	}[]
}

export type LoanCalculationCalculatedData = {
	priceAdjustments: number // Verðbætur - not sure what this is
	indexationOnInterest: number // Verðbætur v/vaxta - not sure what this is

	avgPayment: number // Meðalgreiðsla - not sure what this is
	// Summary
	nextPayment: number
	// Financial plan
	totalRepayment: number
	annualPercentageRate: number

	totalPrincipal: number // Höfuðstóll
	disbursementFee: number // Lántökukostnaður
	disbursement: number // Útborguð upphæð að frádregnum kostnaði
	totalPaymentFee: number // Greiðslugjald
	interestRate: number

	// Require manual mapping
	estimatedEquityAfterXYears: number // kalkulacija iz tablice - iz tablice - 60 number - propertyValue - Áætluð eign eftir 5 ár
	reductionPaymentAfterXYears: number

	installments: PaymentPlanInstallment[]
	settlementCosts: CalculatorDataSettlementCosts[]
	graphData: {
		year: number
		value: number
	}[]
	monthlyInstallementChartData: {
		year: number
		value: number
	}[]
	principalReductionChartData: {
		year: number
		value: number
	}[]
	paymentReductionMinAmount: number
	paymentReductionMaxAmount: number
}

export type CalculatorDataSettlementCosts = {
	rateId: number
	amount: number
	costId: number
	currency: string
	displayName: string
	percent: number
}

export type PaymentPlanInstallment = {
	number: number // Greiðsla
	dueDate: string // Dagsetning
	principal: number // Afborgun
	interest: number // Vextir
	paymentFee: number // Greiðslugjald
	total: number // Greitt samtals
	remainingPrincipal: number
	propertyValue: number // installments - propertyValue
	propertyEquity: number
}

export type PaymentPlanData = {
	totalPrincipal: number // Höfuðstóll
	disbursementFee: number // Lántökukostnaður
	disbursement: number // Útborguð upphæð að frádregnum kostnaði
	priceAdjustments: number // Verðbætur
	indexationOnInterest: number // Verðbætur v/vaxta
	totalPaymentFee: number // Greiðslugjald
	installments: {
		number: number // Greiðsla
		dueDate: string // Dagsetning
		principal: number // Afborgun
		interest: number // Vextir
		paymentFee: number // Greiðslugjald
		total: number // Greitt samtals
		remainingPrincipal: number
		propertyValue: number // installments - propertyValue
		propertyEquity: number
	}[]
	showAllDueDates: boolean // Sýna alla gjalddaga
}

export type PaymentPlanTableRowData = {
	index: number // Greiðsla
	date: string // Dagsetning
	installment: number // Afborgun
	interest: number // Vextir
	priceAdjustments: number // Verðbætur
	paymentFee: number // Greiðslugjald
	totalPaid: number // Greitt samtals
}

export type GetMortgageSettingsResponse = {
	options: {
		paymentReductionMaxLengthMonths: number // temporaryReduction length?
		paymentReductionInterestRateMinLimit: number // temporaryReduction, not sure what this is.?
		minPrincipalAmount: number // loan amount
		maxPrincipalAmount: number // loan amount
		minLoanLengthMonths: number // numberOfPaymentsMonths
		maxLoanLengthMonths: number // numberOfPaymentsMonths
		mortgageRatio: number // ?
		maxAdditionalPaymentsIndividuals: number // maxAdditionalPaymentsIndividuals
		maxAdditionalPaymentsMarried: number // maxAdditionalPaymentsMarried
		housingMarketGrowth10YrAverage: number // fixedInterestPercentage?
		housingMarketGrowthLastYear: number // fixedInterestPercentage?
		interestRate10YrAverage: number // ?
	}
	interestRates: {
		productType: string
		centralBankInterestRate: number
		additionalFee: number
		futureAdditionalFee: number

		ltvSurchargePerPoint: number
		ltvFutureSurchargePerPoint: number
		ltvBaseThreshold: number
		ltvMaxThreshold: number

		interestRate: number
	}[]
}

export const initialLoanCalculationValuesTypeA: LoanCalculationInputs = {
	loanAmount: 55_000_000,
	propertyMarketValueAmount: 100_000_000,
	annualHousingPriceIncreasePercentage: 0,
	estimatedPropertyAnnual10YearOption: true,
	optionBAmount: 0,
	optionBLengthInYears: 0,
	firstPaymentDate: dayjs().toISOString(),

	// Selecting a default plan (you may need to adjust this based on requirements)
	selectedPlan: undefined,

	// Common plan details (assuming same values as before)
	numberOfPaymentsMonths: 40, // Converted to months
	fixedInterestPercentage: undefined,
	extraMonthlyPaymentToPrincipal: 0,

	// Defining principal payment type based on old structure
	principalPaymentType: RepaymentTypeEnum.EqualPayments,
	interestRateOptionFixed: true,
}

export const initialLoanCalculationValuesTypeB: LoanCalculationInputs = {
	loanAmount: 55_000_000,
	propertyMarketValueAmount: 100_000_000,
	annualHousingPriceIncreasePercentage: 0,
	estimatedPropertyAnnual10YearOption: true,
	optionBAmount: 1,
	optionBLengthInYears: 3,
	firstPaymentDate: dayjs().toISOString(),

	// Selecting a default plan (you may need to adjust this based on requirements)
	selectedPlan: undefined,

	// Common plan details (assuming same values as before)
	numberOfPaymentsMonths: 40, // Converted to months
	fixedInterestPercentage: undefined,
	extraMonthlyPaymentToPrincipal: 0,

	// Defining principal payment type based on old structure
	principalPaymentType: RepaymentTypeEnum.EqualPayments,
	interestRateOptionFixed: true,
}

export const initialLoanCalculationCalculatedData: LoanCalculationCalculatedData = {
	priceAdjustments: 0,
	indexationOnInterest: 0,
	avgPayment: 0,
	nextPayment: 0,
	totalRepayment: 0,
	annualPercentageRate: 0,
	totalPrincipal: 0,
	disbursementFee: 0,
	disbursement: 0,
	estimatedEquityAfterXYears: 0,
	graphData: [],
	principalReductionChartData: [],
	reductionPaymentAfterXYears: 0,
	settlementCosts: [],
	totalPaymentFee: 0,
	interestRate: 0,
	installments: [],
	monthlyInstallementChartData: [],
	paymentReductionMaxAmount: 5,
	paymentReductionMinAmount: 100_000,
}
